import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, apiError } from "../lib/api";
import { PageHeader, Card, Empty } from "../components/UI";
import { useRxPanels } from "../components/rx/useRxPanels";
import { RxStatusChip, fmtDate, fmtDateTime } from "../components/rx/rxShared";
import { NewPill } from "../components/NewPill";
import { useAuth } from "../context/AuthContext";
import { PenLine, PhoneOff, Hourglass, Mail, Phone, RefreshCw, PhoneCall } from "lucide-react";
import dayjs from "dayjs";

const SECTIONS = [
  { key: "drafts", title: "Unsigned drafts", icon: PenLine, tone: "text-[#6D4C41] bg-[#EFEBE0]", empty: "No unsigned drafts.", hint: "Drafts you are the named prescriber on." },
  { key: "no_contact", title: "No pharmacy contact yet", icon: PhoneOff, tone: "text-[#C62828] bg-[#FFEBEE]", empty: "Every signed prescription has had pharmacy contact.", hint: "Signed, but no email sent and no call logged. A print alone still counts as waiting." },
  { key: "awaiting", title: "Contacted, not confirmed", icon: Hourglass, tone: "text-[#8D5A00] bg-[#FFF3E0]", empty: "Nothing waiting on confirmation.", hint: "Waiting until a call is logged as called in and confirmed." },
];

const age = (d) => { const n = dayjs().diff(dayjs(d), "day"); return n <= 0 ? "today" : `${n} day${n === 1 ? "" : "s"}`; };

function Row({ r, onOpen }) {
  const lc = r.last_contact;
  return (
    <div className="grid grid-cols-1 gap-2 border-b border-[#F1EEE7] px-4 py-3 last:border-0 md:grid-cols-[1.2fr_1.6fr_1.2fr_auto] md:items-center" data-testid={`dash-row-${r.rx_number}`}>
      <div><Link to={`/clients/${r.client_id}`} data-testid={`dash-client-${r.rx_number}`} className="text-sm font-semibold text-[#3E2723] hover:underline">{r.client_name}</Link>
        <div className="font-mono text-[10px] text-[#84675B]">{r.rx_number}{r.renewed_from ? ` · renewal of ${r.renewed_from.rx_number}` : ""}</div></div>
      <div className="text-sm text-[#5D4037]">{r.medications.join(", ")}</div>
      <div className="text-[11px] text-[#7B6157]">
        <div>{r.status === "draft" ? `Started ${fmtDate(r.created_at)}` : `Signed ${fmtDate(r.signed_at)}`} · {age(r.signed_at || r.created_at)}</div>
        <div>{r.pharmacy_name || "No pharmacy linked"}</div>
        {lc && <div className="flex items-center gap-1">{lc.channel === "email" ? <Mail size={10} /> : <Phone size={10} />} {lc.outcome} · {fmtDateTime(lc.at)}</div>}
      </div>
      <div className="flex items-center gap-2 md:justify-end"><RxStatusChip rx={r} testId={`dash-status-${r.rx_number}`} />
        <button type="button" data-testid={`dash-open-${r.rx_number}`} onClick={() => onOpen(r.id)} className="rounded-full border border-[#E5DFD3] px-3 py-1 text-xs font-semibold text-[#4E342E] transition-colors hover:bg-[#F5F2EB]">Open</button></div>
    </div>
  );
}

/** Prescriber Dashboard: the signed-in prescriber's drafts and prescriptions still waiting on pharmacy contact. */
function NotPrescriber() {
  return (
    <div data-testid="prescriber-dashboard-denied">
      <PageHeader title="Prescriber Dashboard" subtitle="For staff marked as prescribers" />
      <Card className="p-8 text-center"><p className="font-heading text-base font-bold text-[#3E2723]">This dashboard is only for prescribers.</p>
        <p className="mt-1 text-sm text-[#7B6157]">An admin can mark you as a prescriber in Settings, Users and Roles. Prescriptions for a client are on the client's Medical tab.</p>
        <Link to="/" data-testid="dash-denied-home" className="mt-4 inline-block rounded-full border border-[#E5DFD3] px-4 py-2 text-sm font-semibold text-[#4E342E] hover:bg-[#F5F2EB]">Go to the dashboard</Link></Card>
    </div>
  );
}

export default function PrescriberDashboard() {
  const { user } = useAuth();
  return user?.is_prescriber ? <PrescriberBoard /> : <NotPrescriber />;
}

function PrescriberBoard() {
  const [data, setData] = useState(null);
  const [failed, setFailed] = useState(false);
  const load = useCallback(() => api.get("/prescriptions/dashboard").then((r) => { setData(r.data); setFailed(false); })
    .catch((e) => { setFailed(true); toast.error(apiError(e)); }), []);
  const { panels, openRx } = useRxPanels(load);
  useEffect(() => { load(); }, [load]);
  return (
    <div data-testid="prescriber-dashboard">
      <PageHeader title={<span className="inline-flex items-center gap-2">Prescriber Dashboard <NewPill feature="prescriber-dashboard" /></span>}
        subtitle="Your unsigned drafts, and signed prescriptions still waiting on pharmacy contact"
        action={<button type="button" data-testid="dash-refresh" onClick={load} className="flex items-center gap-1.5 rounded-full border border-[#E5DFD3] bg-white px-4 py-2 text-sm font-semibold text-[#4E342E] hover:bg-[#F5F2EB]"><RefreshCw size={14} /> Refresh</button>} />
      <div className="mb-6 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {SECTIONS.map(({ key, title, icon: Icon, tone }) => (
          <a key={key} href={`#dash-${key}`} data-testid={`dash-count-${key}`} className="flex items-center gap-3 rounded-2xl border border-[#EFEBE0] bg-white p-4 transition-shadow hover:shadow-md">
            <span className={`flex h-10 w-10 items-center justify-center rounded-xl ${tone}`}><Icon size={18} /></span>
            <span><span className="block font-heading text-2xl font-bold text-[#3E2723]">{data ? data.counts[key] : failed ? "-" : <span className="inline-block h-6 w-8 animate-pulse rounded bg-[#EFEBE0]" />}</span>
              <span className="text-xs text-[#7B6157]">{title}</span></span>
          </a>
        ))}
        <Link to="/refill-call-queue" data-testid="dash-count-refill-calls" className="flex items-center gap-3 rounded-2xl border border-[#EFEBE0] bg-white p-4 transition-shadow hover:shadow-md">
          <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#FFEBEE] text-[#C62828]"><PhoneCall size={18} /></span>
          <span><span className="block font-heading text-2xl font-bold text-[#3E2723]">{data ? data.refill_calls : "-"}</span>
            <span className="text-xs text-[#7B6157]">Refill cancel calls due</span></span>
        </Link>
      </div>
      <div className="space-y-6">
        {SECTIONS.map(({ key, title, hint, empty }) => (
          <Card key={key} id={`dash-${key}`} className="overflow-hidden" data-testid={`dash-section-${key}`}>
            <div className="border-b border-[#EFEBE0] bg-[#FDFCF8] px-4 py-3"><h2 className="font-heading text-base font-bold text-[#3E2723]">{title}</h2><p className="text-xs text-[#7B6157]">{hint}</p></div>
            {data && (data[key].length ? data[key].map((r) => <Row key={r.id} r={r} onOpen={openRx} />) : <Empty text={empty} />)}
            {failed && !data && <p className="px-4 py-4 text-sm text-[#C62828]" data-testid={`dash-error-${key}`}>Could not load this list. Use Refresh to try again.</p>}
          </Card>
        ))}
      </div>
      {panels}
    </div>
  );
}
